import * as React from 'react';
import {
	CompositionParams,
	SetCompositionState,
	type CompositionState,

} from './Composition';
import { StepDefinition, StepId, StepStatus } from './StepDefinition';

export type ProductWizardState = {
	currentStep: StepDefinition;
	composition: CompositionState;
	steps: StepDefinition[]
	isLastStep: boolean;
};

export type ProductWizardActions = {
	setComposition: SetCompositionState;
	next: () => void;
	back: () => void;
	finish: () => void;
	tryTransitionTo: (stepId: StepId) => Promise<void>;
	getStatus: (stepId: StepId) => StepStatus;
	setSteps: (steps: StepDefinition[], currentStepId: StepId) => void
};

export type ProductWizardMachine = {
	state: ProductWizardState;
	machine: ProductWizardActions;
};


function initStatusById(steps: StepDefinition[], initialStatus?: Exclude<StepStatus, 'completed'>): Record<StepId, StepStatus> {
	return steps.reduce((acc, step) => {
		acc[step.id] = initialStatus ?? 'pending';
		return acc;
	}, {} as Record<StepId, StepStatus>);
}

const getDirection = (from: number, to: number) => ({ isForward: to > from, isBackward: to < from });

// Necesito validar si voy hacia delante o estaba editando y voy hacia atrás
const needsValidation = (isForward: boolean, isBackward: boolean, currentStatus: string) => isForward || (isBackward && currentStatus !== 'pending');

// Puedo ir hacia delante, sí el anterior al destino debe está completedo, o el destino es el siguiente paso inmediato
const isForwardProgressAllowed = (
	steps: Array<{ id: StepId }>,
	targetIndex: number,
	currentStepId: StepId,
	statuses: Record<StepId, StepStatus>
) => {
	const previousStep = steps[Math.max(0, targetIndex - 1)];
	const isNextImmediate = previousStep?.id === currentStepId;
	return statuses[previousStep.id] === 'completed' || isNextImmediate;
};

// Debo guardar el nuevo estado si estaba editando o voy hacia delante
const mustCompleteCurrent = (currentStatus: string, isForward: boolean) => currentStatus === 'review' || isForward;

export function useCompositionWizardMachine(args: {
	params: CompositionParams;
	initialSteps: StepDefinition[];
	initialComposition: CompositionState;
	initialStepsStatus?: Exclude<StepStatus, 'completed'>
	onClose: (composition: CompositionState) => void;
}): ProductWizardMachine {

	const { initialComposition, initialStepsStatus, onClose } = args;

	const [steps, setSteps] = React.useState(args.initialSteps);
	const [composition, setComposition] = React.useState(initialComposition);
	const [statuses, setStatuses] = React.useState(initStatusById(steps, initialStepsStatus));
	const [canFinish, setCanFinish] = React.useState(false);
	const [currentStep, setCurrentStep] = React.useState(steps[0]);

	const validateCurrentStep = React.useCallback(async () => currentStep.validate(composition), [currentStep, composition]);
	const cleanCurrentStep = React.useCallback(() => setComposition(currentStep.clean(composition)), [currentStep, setComposition, composition]);

	const setStatus = React.useCallback((stepId: StepId, status: StepStatus) => {
		setStatuses(prev => ({ ...prev, [stepId]: status }));
	}, []);

	const getStatus = React.useCallback(
		(stepId: StepId) => statuses[stepId],
		[statuses]
	);

	const completeCurrentStep = React.useCallback(() => setStatus(currentStep.id, 'completed'), [currentStep.id, setStatus]);

	// Navegación 'pura': aplica patch (si hay), marca estados y cambia step
	const transitionTo = React.useCallback(
		(nextStep: StepDefinition, mustCompleteCurrentStep?: boolean, mustClearCurrentStep?: boolean) => {

			if (mustCompleteCurrentStep)
				completeCurrentStep();

			if (mustClearCurrentStep)
				cleanCurrentStep();

			// Si vuelvo a un step ya completado, paso a "review"
			if (statuses[nextStep.id] === 'completed') {
				setStatus(nextStep.id, 'review');
			}

			setCurrentStep(nextStep);
		},
		[cleanCurrentStep, completeCurrentStep, setStatus, statuses]
	);

	// Autocierre: último step + todo completed + permite finalizar
	React.useEffect(() => {
		const isLastStep = currentStep.id === steps.last()?.id;
		const allCompleted = Object.values(statuses).every(s => s === 'completed');
		const allowed = currentStep.canFinish || canFinish;

		if (isLastStep && allCompleted && allowed)
			onClose(composition);
	}, [composition, currentStep, canFinish, onClose, statuses, steps]);

	// Decide si se puede ir a stepId. Si puede, devuelve {nextStep, patch} para que goTo lo aplique.
	const canGo = React.useCallback(
		async (stepId: StepId): Promise<{ nextStep: Nullable<StepDefinition>; mustCompleteCurrentStep?: boolean, mustClearCurrentStep?: boolean }> => {

			const targetIndex = steps.findIndex(s => s.id === stepId);
			if (targetIndex < 0)
				throw new Error(`Step ${stepId} no found`);

			const currentIndex = steps.indexOf(currentStep);
			const currentStatus = statuses[currentStep.id];

			const { isForward, isBackward } = getDirection(currentIndex, targetIndex);
			if (!isForward && !isBackward)
				return { nextStep: null };

			const mustValidate = needsValidation(isForward, isBackward, currentStatus);
			const mustComplete = mustCompleteCurrent(currentStatus, isForward);

			const validationResult = (mustValidate || mustComplete) ? await validateCurrentStep() : undefined;
			const isValid = validationResult ?? true;

			const canMoveBackward = isBackward && (currentStatus === 'pending' || isValid);
			const canMoveForward =
				isForward &&
				isValid &&
				isForwardProgressAllowed(steps, targetIndex, currentStep.id, statuses);

			if (!canMoveBackward && !canMoveForward)
				return { nextStep: null };

			return {
				nextStep: steps[targetIndex],
				mustCompleteCurrentStep: isForward,
				mustClearCurrentStep: currentStatus === 'pending' && isBackward
			};
		},
		[steps, currentStep, statuses, validateCurrentStep]
	);

	const tryTransitionTo = React.useCallback(
		async (stepId: StepId) => {
			const { nextStep, mustCompleteCurrentStep, mustClearCurrentStep } = await canGo(stepId);
			if (nextStep === null)
				return;

			transitionTo(nextStep, mustCompleteCurrentStep, mustClearCurrentStep);
		},
		[canGo, transitionTo]
	);

	const isLastStep = React.useMemo(
		() => currentStep.id === steps.last()?.id && steps.length > 0,
		[currentStep.id, steps]
	);

	const getNextStep = React.useCallback(() => {
		const i = steps.indexOf(currentStep);
		return steps[Math.min(i + 1, steps.length - 1)];
	}, [steps, currentStep]);

	const getPreviousStep = React.useCallback(() => {
		const i = steps.indexOf(currentStep);
		return steps[Math.max(i - 1, 0)];
	}, [steps, currentStep]);

	const completeCurrentIfValid = React.useCallback(async () => {
		const isValid = await validateCurrentStep();
		if (!isValid)
			return false;

		completeCurrentStep();
		return true;
	}, [completeCurrentStep, validateCurrentStep]);

	const next = React.useCallback(async () => {

		// Si es el útimo paso, no navego. Lo intento completar y marco que se 
		// puede cerrar el wizard
		if (isLastStep) {
			const success = await completeCurrentIfValid();
			if (!success)
				return;

			setCanFinish(true);
			return;
		}

		const nextStep = getNextStep();
		await tryTransitionTo(nextStep.id);

	}, [isLastStep, completeCurrentIfValid, getNextStep, tryTransitionTo]);

	const back = React.useCallback(async () => {
		const nextStep = getPreviousStep();
		await tryTransitionTo(nextStep.id);
	}, [getPreviousStep, tryTransitionTo]);

	const finish = React.useCallback(async () => next(), [next]);

	const updateSteps = React.useCallback((steps: StepDefinition[], currentStepId: StepId) => {
		const currentStep = steps.find(x => x.id === currentStepId);
		if (!currentStep)
			throw new Error(`Current step ${currentStepId} not found`);
		setSteps(steps);
		setCurrentStep(currentStep);
		setStatuses(initStatusById(steps));
	}, []);

	return {
		state: {
			currentStep,
			composition,
			steps,
			isLastStep: currentStep.id === steps.last()?.id,
		},
		machine: {
			setComposition,
			next,
			back,
			finish,
			tryTransitionTo,
			getStatus,
			setSteps: updateSteps
		},
	};
}
